<template>
  <div>
    <div class="row border-bottom white-bg dashboard-header">
      <div class="col-lg-12">
        <h1>Social Analytics</h1>
      </div>
    </div>
    <div class="dasboard-container">
      <div v-if="loadingChannels">
        <loader message="Fetching Social Channels" shadow="none" />
      </div>
      <div v-else-if="!socialChannels || !socialChannels.length">
        <content-placeholder
          :count="1"
          :text-only="true"
          text-value="You don't have any social channels. Please go to channels to add some."
        />
      </div>
      <div v-else>
        <div class="row row-1">
          <div class="col-md-12">
            <page-demographics-overview
              :demographicData="demographicData"
              :isLoading="demographicsLoading"
              :facebookPages="facebookChannels"
              :selectedPage="selectedFacebookPage"
              :selectedDemographicType="selectedDemographic"
              @onSelectedPageChange="handlePageChange"
              @onSelectedDemographicTypeChanged="handleDemographicTypeChanged"
            />
          </div>
        </div>
        <div class="row row-2">
          <div class="col-md-6">
            <facebook-insights-overview
              class="insights"
              :isLoading="insightsOverviewLoading"
              :overviewData="insightOverviewData"
              :facebookPages="facebookChannels"
              :selectedPage="selectedFacebookPage"
              @onSelectedPageChange="handlePageChange"
            />
          </div>
          <div class="col-md-6">
            <instagram-insights-overview
              class="insights"
              @onInstagramPeriodChanged="fetchInstagramInsightsOverviewData"
              :isLoading="instagramInsightsOverviewLoading"
              :overviewData="instagramInsightOverviewData"
              :instagramPages="instagramChannels"
              :selectedPage="selectedInstagramPage"
              @onSelectedPageChange="handleInstagramPageChange"
            />
          </div>
        </div>
        <div class="row row-3">
          <div class="col-md-12">
            <post-overview
              :isLoading="facebookPostInsightLoading"
              :facebookPages="facebookChannels"
              :postInsights="facebookPostInsightData"
              :selectedPage="selectedFacebookPage"
              :pages="postInsightPageSize"
              @onSelectedPageChange="handlePageChange"
              @changePage="changePostInsightPage"
              @onChangeSerachTerm="changeSerachTerm"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex';
import { instagramAPI } from '@/api';
import * as socialTypes from '@/store/modules/social/mutation-types';
import Loader from '@/components/common/Loader';
import ContentPlaceholder from '@/components/common/ContentPlaceholder';

import FacebookInsightsOverview from '@/components/social/facebook/InsightsOverview';
import PageDemographicsOverview from '@/components/social/facebook/PageDemographicsOverview';
import FacebookPostAnalytics from '@/graphql/analytics/facebookPostAnalytics.gql';
import PostOverview from '@/components/social/facebook/PostOverview';
import facebookPageFansDemographics from '@/graphql/analytics/facebookPageFansDemographics.gql';
import facebookPageContentDemographics from '@/graphql/analytics/facebookPageContentDemographics.gql';
import facebookPageImpressionsDemographics from '@/graphql/analytics/facebookPageImpressionsDemographics.gql';

import facebookPageInsightsOverviewQuery from '@/graphql/analytics/facebookPageInsightsOverviewQuery.gql';
import InstagramInsightsOverview from '@/components/social/instagram/InsightsOverview';
import { processDemographicData, processInsightsOverviewData } from './SocialDashboardUtils';

const queryMap = {
  fans: facebookPageFansDemographics,
  engagements: facebookPageImpressionsDemographics,
  content: facebookPageContentDemographics,
};

export default {
  name: 'SocialAnalyticsDashboard',

  data: () => ({
    demographicsLoading: false,
    insightsOverviewLoading: false,
    instagramInsightsOverviewLoading: false,
    facebookPostInsightLoading: false,
    selectedDemographic: 'fans',
    demographicData: null,
    insightOverviewData: null,
    instagramInsightOverviewData: null,
    facebookPostInsightData: null,
    postInsightPageSize: 0,
    currentPostInsightPage: 1,
    postInsightSearchTerm: '',
  }),

  computed: {
    ...mapState('social', ['selectedFacebookPage', 'selectedInstagramPage']),
    ...mapState('channel', ['loadingChannels']),
    ...mapGetters('social', ['facebookInsightsOverviewData', 'instagramInsightsOverviewData']),
    ...mapGetters('channel', ['socialChannels', 'facebookChannels', 'instagramChannels']),
    overviewData() {
      return this.facebookInsightsOverviewData[this.selectedFacebookPage];
    },
  },

  mounted() {
    this.$store.dispatch('channel/getChannels');
  },

  methods: {
    handlePageChange(value) {
      this.$store.commit(`social/${socialTypes.SET_SELECTED_FACEBOOK_PAGE}`, value);
    },

    changeSerachTerm(value) {
      this.postInsightSearchTerm = value;
      this.fetchFacebookPostAnalyticstData();
    },

    handleInstagramPageChange(value) {
      this.$store.commit(`social/${socialTypes.SET_SELECTED_INSTAGRAM_PAGE}`, value);
    },

    handleDemographicTypeChanged(value) {
      this.selectedDemographic = value;
    },

    fetchDemographicsData() {
      if (!this.selectedDemographic || !this.selectedFacebookPage || !this.facebookChannels) return;

      this.demographicsLoading = true;

      this.$apollo
        .query({
          query: queryMap[this.selectedDemographic],
          variables: {
            channelId: this.selectedFacebookPage,
          },
        })
        .then(resp => {
          this.demographicData = processDemographicData(
            Object.values(resp.data.pageDemographics)[0],
          );
          this.demographicsLoading = false;
        });
    },
    fetchinsightsOverviewData() {
      if (!this.selectedFacebookPage || !this.facebookChannels) return;

      this.insightsOverviewLoading = true;

      this.$apollo
        .query({
          query: facebookPageInsightsOverviewQuery,
          variables: {
            channelId: this.selectedFacebookPage,
          },
        })
        .then(resp => {
          const processedData = processInsightsOverviewData(Object.values(resp.data)[0]);
          const data = {};
          Object.keys(processedData).forEach(key => {
            data[key] = processedData[key];
          });

          this.insightOverviewData = Object.assign({}, data);

          this.insightsOverviewLoading = false;
        });
    },
    fetchFacebookPostAnalyticstData() {
      this.facebookPostInsightLoading = true;
      this.$apollo
        .query({
          query: FacebookPostAnalytics,
          variables: {
            page: this.currentPostInsightPage,
            channelId: this.selectedFacebookPage,
            search: this.postInsightSearchTerm,
          },
        })
        .then(resp => {
          const data = resp.data.postInsightsOverview;
          this.facebookPostInsightLoading = false;
          this.facebookPostInsightData = data.objects;
          this.setPostInsightPageData(data);
        })
        .catch(() => {
          this.facebookPostInsightData = [];
          this.facebookPostInsightLoading = false;
        });
    },
    setPostInsightPageData(pageDate) {
      this.postInsightPageSize = pageDate.pages;
    },
    changePostInsightPage(page) {
      this.currentPostInsightPage = page;
      this.fetchFacebookPostAnalyticstData();
    },
    fetchInstagramInsightsOverviewData(period) {
      if (!this.selectedInstagramPage || !this.instagramChannels) return;
      this.instagramInsightsOverviewLoading = true;
      const periodMetrics = {
        day: 'views,reach,profile_views,follows_and_unfollows,follower_count',
        week: 'reach',
        month: 'reach',
        lifetime:
          'reached_audience_demographics,engaged_audience_demographics,follower_demographics,follower_count',
      };
      const metrics = periodMetrics[period];
      const metricsPeriod = period === 'month' ? 'days_28' : period;

      const selectedInstagramChannel = this.instagramChannels.filter(
        channel => channel.id === this.selectedInstagramPage,
      );

      if (selectedInstagramChannel.length > 0) {
        const serviceId = selectedInstagramChannel[0].extra_data.channel_id;
        instagramAPI
          .get(`facebook/insights/?channel=${serviceId}&metrics=${metrics}&period=${metricsPeriod}`)
          .then(response => {
            this.instagramInsightOverviewData = response.data.data;
            this.instagramInsightsOverviewLoading = false;
          });
      }
    },
  },

  watch: {
    facebookChannels() {
      if (this.facebookChannels.length && !this.selectedFacebookPage) {
        this.handlePageChange(this.facebookChannels[0].id);
      }
      this.fetchDemographicsData();
      this.fetchinsightsOverviewData();
      this.fetchFacebookPostAnalyticstData();
    },

    instagramChannels() {
      if (this.instagramChannels.length && !this.selectedInstagramPage) {
        this.handleInstagramPageChange(this.instagramChannels[0].id);
      }
      this.fetchInstagramInsightsOverviewData('day');
    },

    selectedFacebookPage(newVal, oldVal) {
      if (newVal !== oldVal) {
        this.fetchDemographicsData();
        this.fetchinsightsOverviewData();
        this.fetchFacebookPostAnalyticstData();
      }
    },

    selectedInstagramPage(newVal, oldVal) {
      if (newVal !== oldVal) {
        this.fetchInstagramInsightsOverviewData('day');
      }
    },

    selectedDemographic(newVal, oldVal) {
      if (newVal !== oldVal) this.fetchDemographicsData();
    },
  },

  components: {
    PostOverview,
    FacebookInsightsOverview,
    InstagramInsightsOverview,
    PageDemographicsOverview,
    Loader,
    ContentPlaceholder,
  },
};
</script>

<style lang="scss" scoped>
.dasboard-container {
  margin: 15px;
  padding-bottom: 50px;
}
.row {
  margin-top: 13px;
}
.insights {
  min-height: 540px;
}
</style>



// WEBPACK FOOTER //
// SocialAnalyticsDashboard.vue?1a7641b8